import {
  AutoFixHigh as AutoFixHighIcon,
  DeleteOutlineOutlined as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  GroupOutlined as GroupOutlinedIcon,
  History as HistoryIcon,
  LockOutlined as LockOutlinedIcon,
  OpenInNew as OpenInNewIcon,
  PublicOutlined as PublicIcon,
  SettingsBackupRestoreOutlined as SettingsBackupRestoreIcon,
  ShareOutlined as ShareIcon,
  VisibilityOutlined as VisibilityIcon,
} from "@/components/icons";
import * as React from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import { visuallyHidden } from "@mui/utils";

import { DocumentFormatIcon } from "@/features/documents/documentIcon";
import { documentInlineHref } from "@/features/documents/documentLinks";
import { templateLabel } from "@/features/documents/template";

import { DocumentDownloadMenu } from "@/features/documents/DocumentDownloadMenu";
import { ShareLinkDialog } from "@/features/documents/ShareLinkDialog";
import { Link as RouterLink } from "react-router-dom";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { useMe, useRecheckDocumentDrift } from "@/api/hooks";
import type { DocumentSummary } from "@/api/types";
import { useConfirm } from "@/hooks/useConfirm";
import { useOptionalToast } from "@/hooks/useToast";
import { relativeAge, formatDecimal } from "@/i18n/format";
import { useNow } from "@/hooks/useNow";

export type DocumentListVariant = "compact" | "full";

/** What an archive or a sharing change applies to: this one version, or
 *  every version of the document in its session (the compact card). */
export type DocumentScope = "document" | "lineage";

/** Shared document list renderer.
 *
 *  - ``compact`` — a card per DOCUMENT, not per render: re-renders are
 *    versions (``parent_document_id``), so each card shows the latest
 *    version with its earlier versions one click away. Used in the
 *    SessionView right rail.
 *  - ``full`` — table with template, size, timestamps, actions. Used on
 *    the /app/documents library page.
 */
export function DocumentList({
  documents,
  variant = "compact",
  onDelete,
  onRestore,
  onOpenSession,
  onToggleVisibility,
  onIterate,
  orgSharingEnabled = false,
  emptyMessage,
  showSessionLink = false,
  showIterateAction = false,
  hideHeader = false,
}: {
  documents: (DocumentSummary & {
    shared?: boolean;
    author_email?: string | null;
    author_display_name?: string | null;
  })[];
  variant?: DocumentListVariant;
  /** ``scope`` is ``"lineage"`` for the compact card (the whole
   *  document), ``"document"`` for one version. */
  onDelete?: (documentId: string, scope?: DocumentScope) => void;
  /** Optional restore handler — when supplied, archived rows
   *  (``deleted_at != null``) render a Restore button instead of
   *  the standard Archive one. Library page wires this up when
   *  "Show archived" is on. */
  onRestore?: (documentId: string) => void;
  onOpenSession?: (sessionId: string) => void;
  onToggleVisibility?: (
    documentId: string,
    next: "private" | "org",
    scope?: DocumentScope,
  ) => void;
  /** Fired when the user asks to iterate on a document. The caller
   *  navigates to the doc's session with a query param so SessionView
   *  opens the drawer pre-seeded with this doc as the reference. */
  onIterate?: (doc: DocumentSummary) => void;
  orgSharingEnabled?: boolean;
  emptyMessage?: string;
  showSessionLink?: boolean;
  /** Show an "iterate on this doc" button alongside preview/download.
   *  Only meaningful in the library full variant. */
  showIterateAction?: boolean;
  /** Skip rendering the <TableHead> so callers can stack multiple
   *  full-variant tables below a single shared header, keeping the
   *  visual continuity of one table while preserving lineage-scoped
   *  interaction. Only honoured when variant="full". */
  hideHeader?: boolean;
}): React.JSX.Element {
  const confirm = useConfirm();
  const { t } = useTranslation();
  // Hooks before the empty-list early return.
  const versions = React.useMemo(() => latestVersions(documents ?? []), [documents]);
  // This list re-renders on document changes only — tick the ages
  // along on our own so a long-open library page keeps them fresh.
  const now = useNow();
  const formatAgoLocal = (ts: string): string => {
    try {
      return relativeAge(ts, now);
    } catch {
      return "";
    }
  };
  const requestDelete = async (doc: DocumentSummary): Promise<void> => {
    if (!onDelete) return;
    const ok = await confirm({
      // Honest verb: this is a soft delete — the row moves to the
      // archived view and can be restored from there.
      title: t("documents_ui.archive_confirm_title", "Archive document"),
      message: t(
        "documents_ui.archive_confirm_body",
        "Archive “{{title}}”? You can restore it later from the archived view.",
        { title: doc.title },
      ),
      confirmLabel: t("documents_ui.archive_confirm_cta", "Archive"),
      destructive: true,
    });
    if (ok) onDelete(doc.id);
  };
  // The compact card is the whole document: its trash archives every
  // version, and says how many.
  const requestArchiveDocument = async (
    doc: DocumentSummary,
    earlierCount: number,
  ): Promise<void> => {
    if (!onDelete) return;
    const ok = await confirm({
      title: t("documents_ui.archive_confirm_title"),
      message:
        earlierCount > 0
          ? t(
              earlierCount === 1
                ? "documents_ui.archive_document_confirm_body_one"
                : "documents_ui.archive_document_confirm_body_other",
              { title: doc.title, count: earlierCount },
            )
          : t("documents_ui.archive_confirm_body", { title: doc.title }),
      confirmLabel: t("documents_ui.archive_confirm_cta"),
      destructive: true,
    });
    if (ok) onDelete(doc.id, "lineage");
  };
  const requestArchiveVersion = async (doc: DocumentSummary): Promise<void> => {
    if (!onDelete) return;
    const ok = await confirm({
      title: t("documents_ui.archive_version_confirm_title"),
      message: t("documents_ui.archive_version_confirm_body", {
        title: doc.title,
        version: doc.version,
      }),
      confirmLabel: t("documents_ui.archive_confirm_cta"),
      destructive: true,
    });
    if (ok) onDelete(doc.id, "document");
  };
  if (!documents || documents.length === 0) {
    return (
      <Typography color="text.secondary" variant="body2" sx={{ py: 1 }}>
        {emptyMessage ?? t("common.documents_empty")}
      </Typography>
    );
  }

  if (variant === "compact") {
    return (
      <Stack sx={{ gap: 1 }}>
        {versions.map(({ doc, earlier, owned }) => (
          <CompactDocumentCard
            key={doc.id}
            doc={doc}
            earlier={earlier}
            ownedCount={owned.length}
            orgSharingEnabled={orgSharingEnabled}
            onToggleVisibility={
              onToggleVisibility
                ? (id, next) => onToggleVisibility(id, next, "lineage")
                : undefined
            }
            onRestore={onRestore}
            onDelete={
              onDelete ? () => void requestArchiveDocument(doc, owned.length) : undefined
            }
            onArchiveVersion={
              onDelete ? (old) => void requestArchiveVersion(old) : undefined
            }
            formatAgo={formatAgoLocal}
          />
        ))}
      </Stack>
    );
  }

  return (
    <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
    <Table
      size="small"
      sx={{
        tableLayout: "fixed",
        width: "100%",
        // Keep the table legible on phones by holding a minimum
        // width — narrower than this, the fixed columns collapse
        // into unreadable slivers. TableContainer scrolls
        // horizontally when the viewport can't fit it.
        minWidth: 720,
      }}
    >
      {/* Fixed column widths keep every lineage's table aligned with
       *  the shared header in DocumentLibraryPage/ProjectGroup. The
       *  document column flexes; all others are pixel-stable so
       *  numbers and timestamps line up column-to-column. */}
      <colgroup>
        <col />
        {/* Template slugs run ``default-v1`` / ``quotation-v1``
            (legacy rows may still carry ``appli-*-v1`` at 16-17
            chars). 180 px fits the full slug without truncating the
            chip label — 140 px clipped the longest in the wild. */}
        <col style={{ width: 180 }} />
        <col style={{ width: 110 }} />
        <col style={{ width: 90 }} />
        <col style={{ width: 130 }} />
        {showSessionLink && <col style={{ width: 90 }} />}
        <col style={{ width: 180 }} />
      </colgroup>
      {!hideHeader && (
        <TableHead>
          <TableRow>
            <TableCell>{t("documents_ui.table_document")}</TableCell>
            <TableCell>{t("documents_ui.table_template")}</TableCell>
            <TableCell>{t("documents_ui.table_visibility")}</TableCell>
            <TableCell align="right">{t("documents_ui.table_size")}</TableCell>
            <TableCell>{t("documents_ui.table_created")}</TableCell>
            {showSessionLink && <TableCell>{t("documents_ui.table_session")}</TableCell>}
            <TableCell align="right">{t("documents_ui.table_actions")}</TableCell>
          </TableRow>
        </TableHead>
      )}
      <TableBody>
        {documents.map((doc) => (
          <TableRow
            key={doc.id}
            // Archived rows read as archived: dimmed + chip. Before
            // this they were pixel-identical to live rows when the
            // "show archived" toggle was on.
            sx={doc.deleted_at ? { opacity: 0.55 } : undefined}
          >
            <TableCell>
              <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                <DocumentFormatIcon format={doc.artifact_format} size={20} />
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
                    <Typography noWrap sx={{ fontWeight: 600 }}>
                      {doc.title}
                    </Typography>
                    {doc.deleted_at && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t("documents_ui.archived_chip", "Archived")}
                        sx={{ height: 18, fontSize: 10 }}
                      />
                    )}
                    {doc.version > 1 && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`v${doc.version}`}
                        sx={{ height: 18, fontSize: 10 }}
                      />
                    )}
                    {(doc as { shared?: boolean }).shared && (
                      <Tooltip title={t("documents_ui.shared_tooltip")}>
                        <ShareIcon sx={{ fontSize: 14, color: "primary.main" }} />
                      </Tooltip>
                    )}
                  </Stack>
                  {doc.summary && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      title={doc.summary}
                      sx={{ display: "block" }}
                    >
                      {doc.summary}
                    </Typography>
                  )}
                  {authorLabel(doc) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      title={doc.author_email ?? ""}
                      sx={{ display: "block" }}
                    >
                      {t("documents_ui.by_author", { name: authorLabel(doc) })}
                    </Typography>
                  )}
                  {doc.tags && doc.tags.length > 0 && (
                    <Stack direction="row" sx={{ gap: 0.25, mt: 0.25 }}>
                      {doc.tags.slice(0, 4).map((tag) => (
                        <Chip
                          key={tag}
                          size="small"
                          variant="outlined"
                          label={tag}
                          sx={{ height: 18, fontSize: 10 }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Stack>
            </TableCell>
            <TableCell>
              {/* Friendly template label ("doc", "deck", "quote"
               *  …) instead of the raw "default-v1" slug —
               *  the latter was internal vocabulary that leaked
               *  into the user-facing table and had to be
               *  decoded on every read. Full slug stays
               *  reachable via the tooltip for operators who
               *  need it for debugging. */}
              <Tooltip title={doc.template_slug}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={templateLabel(doc.template_slug)}
                />
              </Tooltip>
            </TableCell>
            <TableCell>
              <VisibilityChip
                doc={doc}
                orgSharingEnabled={orgSharingEnabled}
                onToggle={onToggleVisibility}
              />
            </TableCell>
            <TableCell align="right">{prettyBytes(doc.pdf_size_bytes)}</TableCell>
            <TableCell>
              <Tooltip title={format(new Date(doc.created_at), "yyyy-MM-dd HH:mm")}>
                <Typography variant="caption">
                  {formatAgoLocal(doc.created_at)}
                </Typography>
              </Tooltip>
            </TableCell>
            {showSessionLink && (
              <TableCell>
                <MuiLink
                  component={RouterLink}
                  to={`/app/sessions/${doc.session_id}`}
                  onClick={
                    onOpenSession
                      ? (e: React.MouseEvent) => {
                          e.preventDefault();
                          onOpenSession(doc.session_id);
                        }
                      : undefined
                  }
                  underline="hover"
                >
                  <Stack direction="row" sx={{ alignItems: "center", gap: 0.25 }}>
                    <Typography variant="caption">{t("documents_ui.open_link")}</Typography>
                    <OpenInNewIcon sx={{ fontSize: 12 }} />
                  </Stack>
                </MuiLink>
              </TableCell>
            )}
            <TableCell align="right">
              <Stack direction="row" sx={{ justifyContent: "flex-end", gap: 0.5 }}>
                {showIterateAction && onIterate && !doc.shared && (
                  <Tooltip title={t("documents_ui.iterate_tooltip")}>
                    <IconButton
                      size="small"
                      onClick={() => onIterate(doc)}
                      aria-label={t("documents_ui.iterate_tooltip")}
                      color="primary"
                    >
                      <AutoFixHighIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={t("documents_ui.preview_tooltip")}>
                  <IconButton
                    size="small"
                    component="a"
                    href={documentInlineHref(doc.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t("common.preview")}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <DocumentDownloadMenu doc={doc} />
                <ShareLinkAction doc={doc} />
                {doc.deleted_at && onRestore ? (
                  <Tooltip
                    title={t(
                      "documents_ui.restore_tooltip",
                      "Restore this archived document back to the library.",
                    )}
                  >
                    <IconButton
                      size="small"
                      onClick={() => onRestore(doc.id)}
                      aria-label={t(
                        "documents_ui.restore_aria",
                        "Restore document",
                      )}
                    >
                      <SettingsBackupRestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  onDelete && (
                    <Tooltip title={t("common.delete")}>
                      <IconButton
                        size="small"
                        onClick={() => void requestDelete(doc)}
                        aria-label={t("common.delete")}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )
                )}
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </TableContainer>
  );
}

/** One document in the compact list (the session's side panel).
 *
 *  The panel is ~320 px wide, and on a phone the whole screen: the
 *  visibility chip and three icon buttons beside the title squeezed it
 *  to "Pr…", the format chip to "D…" and the size and time to one word
 *  per line. So the title gets the full width (two lines, then an
 *  ellipsis, the whole title in a tooltip), the chips a wrapping row of
 *  their own, size and time one unbroken line, and the visibility chip
 *  and actions a row below. */
function CompactDocumentCard({
  doc,
  earlier,
  orgSharingEnabled,
  onToggleVisibility,
  onRestore,
  onDelete,
  onArchiveVersion,
  ownedCount,
  formatAgo,
}: {
  doc: DocumentSummary & {
    shared?: boolean;
    author_email?: string | null;
    author_display_name?: string | null;
  };
  /** The document's earlier versions, newest first. */
  earlier: DocumentSummary[];
  /** How many of ``earlier`` the card's archive and sharing change: the
   *  ones no other card also shows. */
  ownedCount: number;
  orgSharingEnabled: boolean;
  /** Changes the sharing of every version (the caller scopes it). */
  onToggleVisibility?: (documentId: string, next: "private" | "org") => void;
  onRestore?: (documentId: string) => void;
  /** Archives the whole document, every version. */
  onDelete?: () => void;
  /** Archives one earlier version. */
  onArchiveVersion?: (version: DocumentSummary) => void;
  formatAgo: (ts: string) => string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const author = authorLabel(doc);
  const [showEarlier, setShowEarlier] = React.useState(false);
  const earlierListId = React.useId();
  return (
    <Box
      data-testid="document-card"
      sx={{
        p: 1.25,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        minWidth: 0,
      }}
    >
      <Stack direction="row" sx={{ alignItems: "flex-start", gap: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", flexShrink: 0, pt: 0.25 }}>
          <DocumentFormatIcon format={doc.artifact_format} size={20} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={doc.title} placement="top-start" enterDelay={400}>
            <Typography
              data-testid="document-card-title"
              sx={{
                fontWeight: 500,
                lineHeight: 1.35,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                overflowWrap: "anywhere",
              }}
            >
              {doc.title}
            </Typography>
          </Tooltip>
          <Stack
            direction="row"
            sx={{ alignItems: "center", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}
          >
            <FormatChip format={doc.artifact_format} />
            {doc.version > 1 && (
              <Tooltip
                describeChild
                title={t("documents_ui.version_label", { version: doc.version })}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  color="primary"
                  label={<VersionLabel version={doc.version} />}
                  sx={{ height: 18, fontSize: 11, fontWeight: 600 }}
                />
              </Tooltip>
            )}
            {doc.deleted_at && (
              <Chip
                size="small"
                variant="outlined"
                color="default"
                label={t("documents_ui.archived_chip", "Archived")}
                sx={{ height: 18, fontSize: 11 }}
              />
            )}
            <DriftChip doc={doc} />
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{
              mt: 0.25,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {prettyBytes(doc.pdf_size_bytes)} ·{" "}
            <Tooltip
              title={format(new Date(doc.created_at), "yyyy-MM-dd HH:mm")}
              placement="top"
            >
              <Box component="span" sx={{ cursor: "help" }}>
                {formatAgo(doc.created_at)}
              </Box>
            </Tooltip>
          </Typography>
          {author && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={doc.author_email ?? ""}
              sx={{ display: "block" }}
            >
              {t("documents_ui.by_author", { name: author })}
            </Typography>
          )}
          <ContributorRow doc={doc} />
        </Box>
      </Stack>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 0.5,
          mt: 1,
        }}
      >
        <VisibilityChip
          doc={doc}
          orgSharingEnabled={orgSharingEnabled}
          onToggle={onToggleVisibility}
          allVersions={ownedCount > 0}
        />
        <Stack direction="row" sx={{ alignItems: "center", gap: 0.25, ml: "auto" }}>
          <Tooltip title={t("documents_ui.preview_tooltip")}>
            <IconButton
              size="small"
              component="a"
              href={documentInlineHref(doc.id)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("common.preview")}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <DocumentDownloadMenu doc={doc} />
          <ShareLinkAction doc={doc} />
          {doc.deleted_at && onRestore ? (
            <Tooltip
              title={t(
                "documents_ui.restore_tooltip",
                "Restore this archived document back to the library.",
              )}
            >
              <IconButton
                size="small"
                onClick={() => onRestore(doc.id)}
                aria-label={t("documents_ui.restore_aria", "Restore document")}
              >
                <SettingsBackupRestoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            onDelete && (
              <Tooltip title={t("common.delete")}>
                <IconButton
                  size="small"
                  onClick={onDelete}
                  aria-label={t("common.delete")}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )
          )}
        </Stack>
      </Stack>
      {earlier.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          <Tooltip
            describeChild
            title={
              showEarlier
                ? t("documents_ui.older_versions_hide")
                : t("documents_ui.older_versions_show")
            }
          >
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={() => setShowEarlier((v) => !v)}
              aria-expanded={showEarlier}
              aria-controls={earlierListId}
              startIcon={<HistoryIcon sx={{ fontSize: 16 }} />}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    fontSize: 16,
                    transform: showEarlier ? "rotate(180deg)" : undefined,
                    transition: "transform 120ms",
                  }}
                />
              }
              sx={{
                px: 0.5,
                minWidth: 0,
                textTransform: "none",
                color: "text.secondary",
                fontWeight: 500,
              }}
            >
              {t(
                earlier.length === 1
                  ? "documents_ui.older_versions_one"
                  : "documents_ui.older_versions_other",
                { count: earlier.length },
              )}
            </Button>
          </Tooltip>
          <Collapse in={showEarlier} unmountOnExit>
            <Stack
              component="ul"
              id={earlierListId}
              sx={{
                listStyle: "none",
                m: 0,
                mt: 0.5,
                p: 0,
                pt: 0.5,
                gap: 0.25,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              {earlier.map((old) => (
                <Stack
                  component="li"
                  key={old.id}
                  direction="row"
                  sx={{ alignItems: "center", gap: 0.75, minWidth: 0 }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, flexShrink: 0, minWidth: 22 }}
                  >
                    <VersionLabel version={old.version} />
                  </Typography>
                  <VisibilityMark visibility={old.visibility} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    title={format(new Date(old.created_at), "yyyy-MM-dd HH:mm")}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {prettyBytes(old.pdf_size_bytes)} · {formatAgo(old.created_at)}
                  </Typography>
                  <Tooltip title={t("documents_ui.preview_tooltip")}>
                    <IconButton
                      size="small"
                      component="a"
                      href={documentInlineHref(old.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t("documents_ui.preview_version_aria", {
                        version: old.version,
                      })}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <DocumentDownloadMenu
                    doc={old}
                    ariaLabel={t("documents_ui.download_version_aria", {
                      version: old.version,
                    })}
                  />
                  {onArchiveVersion && (
                    <Tooltip
                      title={t("documents_ui.archive_version_aria", {
                        version: old.version,
                      })}
                    >
                      <IconButton
                        size="small"
                        onClick={() => onArchiveVersion(old)}
                        aria-label={t("documents_ui.archive_version_aria", {
                          version: old.version,
                        })}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}

/** "Share public link" (FW-35) for a live document, where the platform
 *  has public links on. Whether the document's organisation allows them
 *  is the dialog's to say: it tells a member to ask an admin. */
function ShareLinkAction({ doc }: { doc: DocumentSummary }): React.JSX.Element | null {
  const { t } = useTranslation();
  const me = useMe();
  const [open, setOpen] = React.useState(false);
  if (me.data?.features?.public_share_links_enabled !== true || doc.deleted_at) {
    return null;
  }
  return (
    <>
      <Tooltip title={t("share_links.action")}>
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          aria-label={t("share_links.action")}
        >
          <PublicIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {open && <ShareLinkDialog doc={doc} open={open} onClose={() => setOpen(false)} />}
    </>
  );
}

/** "v3" for the eye, "Version 3 of this document" for a screen reader.
 *  Visually hidden text rather than an ``aria-label``: screen readers
 *  often skip the label of a non-interactive element. */
export function VersionLabel({ version }: { version: number }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <span aria-hidden="true">{`v${version}`}</span>
      <Box component="span" sx={visuallyHidden}>
        {t("documents_ui.version_label", { version })}
      </Box>
    </>
  );
}

function authorLabel(
  doc: { author_display_name?: string | null; author_email?: string | null },
): string | null {
  const name = (doc.author_display_name ?? "").trim();
  if (name) return name;
  const email = (doc.author_email ?? "").trim();
  if (!email) return null;
  // Fallback: local-part of the email so we never print the full
  // address in tight cells (the title attribute carries the full one).
  const at = email.indexOf("@");
  return at > 0 ? email.slice(0, at) : email;
}

function prettyBytes(n: number): string {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${formatDecimal(n / 1024, 1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${formatDecimal(n / (1024 * 1024), 1)} MB`;
  return `${formatDecimal(n / (1024 * 1024 * 1024), 2)} GB`;
}


// Re-export for callers that want to reuse the helper without
// re-implementing (e.g., the Documents library page's header stats).
// Splitting into a sibling utils file would touch ~5 import sites
// for a Fast Refresh nicety; keeping them here is the lower-cost
// trade.
// eslint-disable-next-line react-refresh/only-export-components
export { prettyBytes };

/** Tiny chip that surfaces the document's authoritative artefact
 *  format ("DOCX" / "PPTX" / "PDF") next to the icon. The icon
 *  alone is fast at-a-glance vocabulary; the chip clarifies for
 *  users who haven't internalised the icon mapping yet. Returns
 *  null when the format is unknown — the icon already falls back
 *  to a neutral document glyph in that case. */
function FormatChip({
  format,
}: {
  format: DocumentSummary["artifact_format"] | null | undefined;
}): React.JSX.Element | null {
  if (!format) return null;
  const label =
    format === "docx"
      ? "DOCX"
      : format === "pptx"
        ? "PPTX"
        : format === "pdf"
          ? "PDF"
          : String(format).toUpperCase();
  return (
    <Chip
      size="small"
      variant="outlined"
      label={label}
      sx={{
        height: 16,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 0.5,
      }}
    />
  );
}

/** Secondary line that lists contributors beyond the primary author.
 *  Renders nothing when the document has zero or one entries (the
 *  one-entry case is the author already shown in the line above).
 *  Users who collaborated on the generating session get a
 *  ``+ Alice, Bob`` suffix in muted type — surfaces collaboration
 *  without burying the row in chips. */
function ContributorRow({
  doc,
}: {
  doc: DocumentSummary & { author_display_name?: string | null };
}): React.JSX.Element | null {
  const contributors = doc.contributors ?? [];
  if (contributors.length <= 1) return null;
  // Skip the entry that matches the author label — the line above
  // already surfaces them. Compare on the display label since
  // contributors is a list of display names, not emails.
  const author = doc.author_display_name ?? null;
  // Two people can share a name: the stored list keeps both entries,
  // the row shows the name once, as the document's cover does.
  const others = [...new Set(contributors.map((c) => c.trim()))].filter(
    (c) => c !== "" && (!author || c.toLowerCase() !== author.toLowerCase()),
  );
  if (others.length === 0) return null;
  // Cap at 3 visible names; overflow becomes "+N more". Tooltip
  // surfaces the full list so a 12-person session doesn't lose the
  // long tail.
  const visible = others.slice(0, 3);
  const overflow = others.length - visible.length;
  return (
    <Tooltip title={others.join(", ")}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 0.25 }}
      >
        + {visible.join(", ")}
        {overflow > 0 ? ` +${overflow} more` : ""}
      </Typography>
    </Tooltip>
  );
}

/** Render a "X commits behind" chip when the scheduled drift checker
 *  has flagged any source as out-of-date. The headline number comes
 *  from the primary's drift count; if other sources also drift,
 *  appends a "+N" hint. Hover shows the full per-source breakdown.
 *
 *  Renders nothing when no drift is detected, when the check hasn't
 *  run yet (``drift_status`` empty / unset), or when the document
 *  has no ``source_commits`` baseline (legacy rows). The "freshness
 *  unknown" entries (force-pushed away, repo gone) surface as a
 *  separate subdued chip so the user knows the staleness is
 *  unverified.
 *
 *  Clicking rechecks. A failed recheck (429 after too many rechecks
 *  a minute, 404 when access is gone, a network error) becomes a
 *  toast rather than an unhandled rejection the user never sees.
 */
export function DriftChip({ doc }: { doc: DocumentSummary }): React.JSX.Element | null {
  // Unconditional hooks first — the early returns below must never
  // change the hook order between renders (rules-of-hooks).
  const { t } = useTranslation();
  // Document lists also render in bare trees (tests, no provider).
  const toast = useOptionalToast();
  const recheck = useRecheckDocumentDrift();
  const sources = doc.source_commits ?? [];
  const drift = doc.drift_status ?? [];
  if (sources.length === 0 || drift.length === 0) return null;

  // Match drift entries to sources by (repository_id, branch_name)
  // — the same key the checker writes against. Sources without a
  // matching drift row are "not yet checked"; we render nothing
  // for those vs. claiming "fresh".
  const driftByKey = new Map<
    string,
    {
      behind: number | null;
      error?: string;
      checkedAt: string;
    }
  >();
  for (const d of drift) {
    driftByKey.set(`${d.repository_id}::${d.branch_name}`, {
      behind: d.behind_commits,
      error: d.error,
      checkedAt: d.checked_at,
    });
  }

  type Decorated = {
    role: "primary" | "ref";
    pathWithNamespace: string;
    branch: string;
    behind: number | null;
    error?: string;
  };
  const decorated: Decorated[] = [];
  for (const s of sources) {
    const d = driftByKey.get(`${s.repository_id}::${s.branch_name}`);
    if (!d) continue;
    decorated.push({
      role: s.role,
      pathWithNamespace: s.path_with_namespace,
      branch: s.branch_name,
      behind: d.behind,
      error: d.error,
    });
  }
  if (decorated.length === 0) return null;

  const drifted = decorated.filter(
    (d) => (d.behind ?? 0) > 0 || d.error,
  );
  if (drifted.length === 0) return null;

  // Headline: primary's behind_commits if it drifted, otherwise
  // the first drifted entry. ``+N`` tail = count of OTHER sources
  // that also drift — always linked projects, since the primary is
  // the headline whenever it drifts. ``drifted[0]`` is guaranteed
  // non-undefined by the length-0 short-circuit above.
  const primary = drifted.find((d) => d.role === "primary");
  const headline = primary ?? (drifted[0] as Decorated);
  const others = drifted.filter((d) => d !== headline);

  const headlineText = headline.error
    ? t("documents_ui.drift_unknown")
    : t("documents_ui.drift_behind", { count: headline.behind ?? 0 });
  const label =
    others.length > 0
      ? t("documents_ui.drift_with_linked", {
          label: headlineText,
          count: others.length,
        })
      : headlineText;

  // Per-source tooltip breakdown — newline-separated so the
  // ``pre-line`` box keeps the layout readable. The checker's error
  // is a stable code; known ones get their own line, anything else
  // a generic "couldn't check" (never the raw code). Trailing
  // "click to recheck now" so users discover the affordance.
  const breakdown = decorated
    .map((d) => {
      const vars = {
        role:
          d.role === "primary"
            ? t("documents_ui.drift_role_primary")
            : t("documents_ui.drift_role_linked"),
        repo: d.pathWithNamespace,
        branch: d.branch,
      };
      switch (d.error) {
        case undefined:
        case "":
          break;
        case "repo_missing":
          return t("documents_ui.drift_line_repo_missing", vars);
        case "branch_missing":
          return t("documents_ui.drift_line_branch_missing", vars);
        case "diverged":
          return t("documents_ui.drift_line_diverged", vars);
        default:
          return t("documents_ui.drift_line_unknown", vars);
      }
      const n = d.behind ?? 0;
      return n === 0
        ? t("documents_ui.drift_line_up_to_date", vars)
        : t("documents_ui.drift_line_behind", { ...vars, count: n });
    })
    .join("\n");
  const tooltipBody = `${breakdown}\n\n${t("documents_ui.drift_recheck_hint")}`;

  const onRecheck = () => {
    recheck.mutate(doc.id, {
      onError: (err) => {
        const status = (err as { response?: { status?: number } } | null)?.response
          ?.status;
        toast?.(
          status === 429
            ? t("documents_ui.drift_recheck_rate_limited")
            : t("documents_ui.drift_recheck_failed"),
          status === 429 ? "info" : "error",
        );
      },
    });
  };

  return (
    <Tooltip title={<Box sx={{ whiteSpace: "pre-line" }}>{tooltipBody}</Box>}>
      <Chip
        size="small"
        variant="outlined"
        color={headline.error ? "default" : "warning"}
        clickable
        onClick={onRecheck}
        disabled={recheck.isPending}
        label={label}
        sx={{ height: 16, fontSize: 10 }}
      />
    </Tooltip>
  );
}

/** An earlier version's sharing, read-only: the card's chip changes
 *  every version at once. Icon for the eye, words for a screen reader. */
function VisibilityMark({
  visibility,
}: {
  visibility: DocumentSummary["visibility"];
}): React.JSX.Element {
  const { t } = useTranslation();
  const isOrg = visibility === "org";
  const text = isOrg
    ? t("documents_ui.visibility_org_owned")
    : t("documents_ui.visibility_private");
  return (
    <Tooltip describeChild title={text}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          flexShrink: 0,
          color: isOrg ? "primary.main" : "text.secondary",
        }}
      >
        {isOrg ? (
          <GroupOutlinedIcon aria-hidden sx={{ fontSize: 16 }} />
        ) : (
          <LockOutlinedIcon aria-hidden sx={{ fontSize: 16 }} />
        )}
        <Box component="span" sx={visuallyHidden}>
          {text}
        </Box>
      </Box>
    </Tooltip>
  );
}

function VisibilityChip({
  doc,
  orgSharingEnabled,
  onToggle,
  allVersions = false,
}: {
  doc: DocumentSummary & { shared?: boolean };
  orgSharingEnabled: boolean;
  onToggle?: (documentId: string, next: "private" | "org") => void;
  /** The toggle changes every version of the document (the card). */
  allVersions?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const isOrg = doc.visibility === "org";
  const owned = !(doc as { shared?: boolean }).shared;
  const label = isOrg ? t("documents_ui.visibility_org_chip") : t("documents_ui.visibility_private_chip");
  const icon = isOrg ? (
    <GroupOutlinedIcon sx={{ fontSize: 14 }} />
  ) : (
    <LockOutlinedIcon sx={{ fontSize: 14 }} />
  );
  // Non-owners see a read-only chip; owners get a click-to-toggle
  // affordance when the backend flag is on.
  if (!owned || !orgSharingEnabled || !onToggle) {
    return (
      <Tooltip
        title={
          isOrg
            ? owned
              ? t("documents_ui.visibility_org_owned")
              : t("documents_ui.visibility_org_shared")
            : t("documents_ui.visibility_private")
        }
      >
        <Chip
          size="small"
          variant="outlined"
          icon={icon}
          label={label}
          color={isOrg ? "primary" : "default"}
          sx={{ "& .MuiChip-icon": { marginLeft: 0.5 } }}
        />
      </Tooltip>
    );
  }
  return (
    <Tooltip
      title={
        allVersions
          ? isOrg
            ? t("documents_ui.visibility_make_private_all")
            : t("documents_ui.visibility_make_org_all")
          : isOrg
            ? t("documents_ui.visibility_make_private")
            : t("documents_ui.visibility_make_org")
      }
    >
      <Chip
        size="small"
        clickable
        variant="outlined"
        icon={icon}
        label={label}
        color={isOrg ? "primary" : "default"}
        onClick={() => onToggle(doc.id, isOrg ? "private" : "org")}
        sx={{ "& .MuiChip-icon": { marginLeft: 0.5 } }}
      />
    </Tooltip>
  );
}

/** Group a flat doc list by the unique project combination linked to
 *  each doc's generating session — primary project + any context_refs.
 *  A session linked to "A" and "B" groups separately from sessions
 *  linked to just "A", so the library surfaces the exact reading
 *  context used to produce each document.
 *
 *  ``projects`` is the sorted, de-duplicated list of project paths in
 *  the group; ``project`` is a display label (join of paths, or
 *  "Unknown project" for docs with no linked repo). Entries are sorted
 *  by most-recent-doc DESC.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function groupDocumentsByProject(
  docs: (DocumentSummary & {
    project_path?: string | null;
    project_paths?: string[] | null;
  })[],
): Array<{
  project: string;
  projects: string[];
  docs: DocumentSummary[];
  latestCreatedAt: string;
}> {
  const groups = new Map<string, { projects: string[]; docs: DocumentSummary[] }>();
  for (const d of docs) {
    const rawPaths = d.project_paths && d.project_paths.length > 0
      ? d.project_paths
      : d.project_path
        ? [d.project_path]
        : [];
    const normalised = Array.from(new Set(rawPaths.map((p) => String(p).trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    const key = normalised.length > 0 ? normalised.join(" + ") : "Unknown project";
    const bucket = groups.get(key) ?? { projects: normalised, docs: [] };
    bucket.docs.push(d);
    groups.set(key, bucket);
  }
  const out: Array<{
    project: string;
    projects: string[];
    docs: DocumentSummary[];
    latestCreatedAt: string;
  }> = [];
  for (const [project, bucket] of groups.entries()) {
    bucket.docs.sort((a, b) => b.created_at.localeCompare(a.created_at));
    const first = bucket.docs[0];
    out.push({
      project,
      projects: bucket.projects,
      docs: bucket.docs,
      latestCreatedAt: first?.created_at ?? "",
    });
  }
  out.sort((a, b) => b.latestCreatedAt.localeCompare(a.latestCreatedAt));
  return out;
}

/** One entry per document: each latest version (a row no other row
 *  names as its parent), with its earlier versions (its parent chain)
 *  newest first. Mirrors the session list's ``document_count``: a
 *  document branched off an earlier version (two renders parented on
 *  v1) is two entries, each listing the versions it continues. Entries
 *  keep the input's newest-first order.
 *
 *  ``owned`` is the part of ``earlier`` no other entry lists: what the
 *  card's archive and sharing change besides its latest version, by the
 *  same rule as the backend's ``session_lineage`` (an ancestor another
 *  latest version continues is left alone). */
// eslint-disable-next-line react-refresh/only-export-components
export function latestVersions<T extends DocumentSummary>(
  docs: T[],
): Array<{ doc: T; earlier: T[]; owned: T[] }> {
  const byId = new Map(docs.map((d) => [d.id, d]));
  const parentOf = (d: T): T | undefined =>
    d.parent_document_id ? byId.get(d.parent_document_id) : undefined;
  const hasNext = new Set<string>();
  for (const d of docs) {
    const parent = parentOf(d);
    if (parent && parent.id !== d.id) hasNext.add(parent.id);
  }
  const entries: Array<{ doc: T; earlier: T[]; owned: T[] }> = [];
  const listed = new Set<string>();
  const listings = new Map<string, number>();
  for (const head of docs) {
    if (hasNext.has(head.id)) continue;
    const earlier: T[] = [];
    const seen = new Set([head.id]);
    for (let d = parentOf(head); d && !seen.has(d.id); d = parentOf(d)) {
      seen.add(d.id);
      earlier.push(d);
    }
    for (const id of seen) {
      listed.add(id);
      listings.set(id, (listings.get(id) ?? 0) + 1);
    }
    entries.push({ doc: head, earlier, owned: [] });
  }
  for (const entry of entries) {
    entry.owned = entry.earlier.filter((d) => listings.get(d.id) === 1);
  }
  // A parent cycle (which the database can't rule out) leaves rows
  // with a next version but no latest one: list them on their own.
  for (const d of docs) {
    if (!listed.has(d.id)) entries.push({ doc: d, earlier: [], owned: [] });
  }
  return entries;
}

/** Cluster documents into lineage chains.
 *
 *  A lineage is every document reachable through ``parent_document_id``
 *  links plus their descendants. Standalone docs (no parent, no
 *  children) come back as single-member lineages so the caller can
 *  render them uniformly. Ordered newest-head-first within the input
 *  list's order.
 *
 *  Each returned lineage is sorted ``v1 → vN`` so the library page can
 *  render the chain progressively.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function groupDocumentsByLineage<
  T extends DocumentSummary & { parent_document_id: string | null },
>(docs: T[]): Array<{ head: T; chain: T[]; latestCreatedAt: string }> {
  const byId = new Map<string, T>();
  for (const d of docs) byId.set(d.id, d);

  // Walk each doc up to its root via parent_document_id links. The
  // visited set guards against pathological cycles (A.parent = B,
  // B.parent = A) that the DB FK alone doesn't prevent.
  const roots = new Set<string>();
  for (const d of docs) {
    let cursor: T | undefined = d;
    const visited = new Set<string>();
    while (cursor && cursor.parent_document_id && byId.has(cursor.parent_document_id)) {
      if (visited.has(cursor.id)) break;
      visited.add(cursor.id);
      cursor = byId.get(cursor.parent_document_id);
    }
    if (cursor) roots.add(cursor.id);
  }

  // BFS descendants from each root. Chains are small (linear v1→v2→…)
  // but the helper accepts branched lineages too. Same visited guard
  // as above so a cycle can't turn the traversal into an infinite
  // enqueue.
  const childIndex = new Map<string, T[]>();
  for (const d of docs) {
    if (d.parent_document_id) {
      const list = childIndex.get(d.parent_document_id) ?? [];
      list.push(d);
      childIndex.set(d.parent_document_id, list);
    }
  }

  const out: Array<{ head: T; chain: T[]; latestCreatedAt: string }> = [];
  for (const rootId of roots) {
    const root = byId.get(rootId);
    if (!root) continue;
    const chain: T[] = [];
    const seen = new Set<string>();
    const queue: T[] = [root];
    while (queue.length > 0) {
      const next = queue.shift();
      if (!next || seen.has(next.id)) continue;
      seen.add(next.id);
      chain.push(next);
      const kids = childIndex.get(next.id) ?? [];
      // Sort kids by created_at ASC so a lineage reads oldest-first.
      kids.sort((a, b) => a.created_at.localeCompare(b.created_at));
      queue.push(...kids);
    }
    chain.sort((a, b) => a.version - b.version || a.created_at.localeCompare(b.created_at));
    const head = chain[chain.length - 1] ?? root;
    const latestCreatedAt = chain.reduce(
      (acc, d) => (d.created_at > acc ? d.created_at : acc),
      "",
    );
    out.push({ head, chain, latestCreatedAt });
  }
  out.sort((a, b) => b.latestCreatedAt.localeCompare(a.latestCreatedAt));
  return out;
}
